import http from './http';
import { buildComplianceEducationApiUrl, toComplianceEducationApiUrl } from '../utils/buildComplianceEducationApiUrl';
import { Account } from '../types/Account';

/**
 * Response structure for accounts list endpoint
 */
export interface AccountsListResponse {
  success: boolean;
  data: Account[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  message?: string;
}

/**
 * Response structure for account users endpoint
 */
export interface AccountUser {
  id: string;
  email: string;
  role: string;
  roleStatus: string;
  status: string;
}

export interface AccountUsersResponse {
  success: boolean;
  data: AccountUser[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Response structure for assigned admins endpoint
 * Matches the structure returned by /accounts/{orgId}/assigned-admins
 */
export interface AssignedAdmin {
  id: string;
  name: string;
  email: string;
  role: string;
  roleStatus: string;
  status: string;
}

export interface AssignedAdminsResponse {
  success: boolean;
  data: AssignedAdmin[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Response structure for account libraries endpoint
 */
export interface AccountLibrary {
  id: string;
  name: string;
  description: string | null;
  hasAccess: boolean;
  hasTranslations?: boolean;
  translationsAssigned?: boolean;
}

export interface AccountLibrariesResponse {
  success: boolean;
  data: {
    libraries: AccountLibrary[];
  };
}

/** Legacy sample preview catalog — same three-key shape as CE account libraries. */
export interface SampleLibrariesResponse {
  success: boolean;
  data: {
    'code-of-conduct'?: unknown[];
    'extended-modules'?: unknown[];
    'additional-assets'?: unknown[];
  };
}

/**
 * Response structure for single account endpoint
 */
export interface AccountResponse {
  success: boolean;
  data: Account;
  message?: string;
}

/**
 * Query parameters for fetching accounts list
 */
export interface AccountsQueryParams {
  search?: string;
  page?: number;
  limit?: number;
  status?: 'ACTIVE' | 'EXPIRED' | 'ALL';
  accountType?: string;
  sortBy?: 'accountName' | 'type' | 'expiryDate' | 'status';
  sortOrder?: 'asc' | 'desc';
}

/**
 * Query parameters for fetching account users and assigned admins
 */
export interface AccountUsersQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  role?: string;
  status?: string;
}

/**
 * Update payload for account
 */
export interface UpdateAccountPayload {
  accountName?: string;
  type?: string;
  expiryDate?: string;
  status?: 'ACTIVE' | 'EXPIRED';
}

/**
 * Fetches list of accounts from the Compliance Ethics API
 * Supports filtering, pagination, and sorting
 *
 * Note: Authentication is handled automatically by the http utility (Authorization header with access token)
 *
 * @param params - Query parameters for filtering and pagination
 * @returns Promise resolving to AccountsListResponse
 * @throws Error if the request fails
 */
export async function fetchAccounts(params?: AccountsQueryParams): Promise<AccountsListResponse> {
  const url = toComplianceEducationApiUrl('/accounts');

  // Add query parameters if provided
  if (params) {
    if (params.search) {
      url.searchParams.append('search', params.search);
    }
    if (params.page !== undefined) {
      url.searchParams.append('page', params.page.toString());
    }
    if (params.limit !== undefined) {
      url.searchParams.append('limit', params.limit.toString());
    }
    if (params.status) {
      url.searchParams.append('status', params.status);
    }
    if (params.accountType) {
      url.searchParams.append('accountType', params.accountType);
    }
    if (params.sortBy) {
      url.searchParams.append('sortBy', params.sortBy);
    }
    if (params.sortOrder) {
      url.searchParams.append('sortOrder', params.sortOrder);
    }
  }

  const response = await http.get(url.toString());

  if (!response.ok) {
    let errorText: string;
    try {
      // Try to get error text from response
      errorText = await response.text();
    } catch (e) {
      // If response.text() fails, fall back to status text or generic message
      errorText = response.statusText || `HTTP ${response.status}`;
    }
    throw new Error(`Failed to fetch accounts: ${response.status} ${errorText}`);
  }

  return await response.json();
}

/**
 * Fetches a single account by ID
 *
 * @param accountId - The unique identifier of the account
 * @returns Promise resolving to AccountResponse
 * @throws Error if the request fails
 */
export async function fetchAccountById(accountId: string): Promise<AccountResponse> {
  const url = buildComplianceEducationApiUrl(`/accounts/${accountId}`);

  const response = await http.get(url);

  if (!response.ok) {
    let errorText: string;
    try {
      errorText = await response.text();
    } catch (e) {
      errorText = response.statusText || `HTTP ${response.status}`;
    }
    throw new Error(`Failed to fetch account: ${response.status} ${errorText}`);
  }

  return await response.json();
}

/**
 * Updates an existing account
 *
 * @param accountId - The unique identifier of the account
 * @param payload - The account data to update
 * @returns Promise resolving to AccountResponse
 * @throws Error if the request fails
 */
export async function updateAccount(accountId: string, payload: UpdateAccountPayload): Promise<AccountResponse> {
  const url = buildComplianceEducationApiUrl(`/accounts/${accountId}`);

  const response = await http.put(url, JSON.stringify(payload));

  if (!response.ok) {
    let errorText: string;
    try {
      errorText = await response.text();
    } catch (e) {
      errorText = response.statusText || `HTTP ${response.status}`;
    }
    throw new Error(`Failed to update account: ${response.status} ${errorText}`);
  }

  return await response.json();
}

/**
 * Creates a new account
 *
 * @param payload - The account data to create
 * @returns Promise resolving to AccountResponse
 * @throws Error if the request fails
 */
export async function createAccount(payload: Omit<Account, 'id'>): Promise<AccountResponse> {
  const url = buildComplianceEducationApiUrl('/accounts');

  const response = await http.post(url, JSON.stringify(payload));

  if (!response.ok) {
    let errorText: string;
    try {
      errorText = await response.text();
    } catch (e) {
      errorText = response.statusText || `HTTP ${response.status}`;
    }
    throw new Error(`Failed to create account: ${response.status} ${errorText}`);
  }

  return await response.json();
}

/**
 * Deletes an account by ID
 *
 * @param accountId - The unique identifier of the account to delete
 * @returns Promise resolving to success response
 * @throws Error if the request fails
 */
export async function deleteAccount(accountId: string): Promise<{ success: boolean; message?: string }> {
  const url = buildComplianceEducationApiUrl(`/accounts/${accountId}`);

  const response = await http.delete(url);

  if (!response.ok) {
    let errorText: string;
    try {
      errorText = await response.text();
    } catch (e) {
      errorText = response.statusText || `HTTP ${response.status}`;
    }
    throw new Error(`Failed to delete account: ${response.status} ${errorText}`);
  }

  return await response.json();
}

/**
 * Fetches users for a specific account/organization
 *
 * @param accountId - The unique identifier of the account
 * @param params - Pagination parameters
 * @returns Promise resolving to AccountUsersResponse
 * @throws Error if the request fails
 */
export async function fetchAccountUsers(
  accountId: string,
  params?: AccountUsersQueryParams,
): Promise<AccountUsersResponse> {
  const url = toComplianceEducationApiUrl(`/accounts/${accountId}/users`);

  if (params?.page) {
    url.searchParams.append('page', params.page.toString());
  }
  if (params?.limit) {
    url.searchParams.append('limit', params.limit.toString());
  }
  if (params?.search) {
    url.searchParams.append('search', params.search);
  }
  if (params?.role) {
    url.searchParams.append('role', params.role);
  }
  if (params?.status) {
    url.searchParams.append('status', params.status);
  }

  const response = await http.get(url.toString());

  if (!response.ok) {
    let errorText: string;
    try {
      errorText = await response.text();
    } catch (e) {
      errorText = response.statusText || `HTTP ${response.status}`;
    }
    throw new Error(`Failed to fetch account users: ${response.status} ${errorText}`);
  }

  return await response.json();
}

/**
 * Fetches libraries for a specific account/organization
 *
 * @param accountId - The unique identifier of the account
 * @returns Promise resolving to AccountLibrariesResponse
 * @throws Error if the request fails
 */
export async function fetchAccountLibraries(accountId: string): Promise<AccountLibrariesResponse> {
  const url = buildComplianceEducationApiUrl(`/accounts/${accountId}/libraries`);

  const response = await http.get(url);

  if (!response.ok) {
    let errorText: string;
    try {
      errorText = await response.text();
    } catch (e) {
      errorText = response.statusText || `HTTP ${response.status}`;
    }
    throw new Error(`Failed to fetch account libraries: ${response.status} ${errorText}`);
  }

  return await response.json();
}

/**
 * Fetches the legacy sample preview catalog for an organization (legacy accounts only).
 */
export async function fetchSampleLibraries(accountId: string): Promise<SampleLibrariesResponse> {
  const url = buildComplianceEducationApiUrl(`/accounts/${accountId}/sample-libraries`);

  const response = await http.get(url);

  if (!response.ok) {
    let errorText: string;
    try {
      errorText = await response.text();
    } catch (e) {
      errorText = response.statusText || `HTTP ${response.status}`;
    }
    throw new Error(`Failed to fetch sample libraries: ${response.status} ${errorText}`);
  }

  return await response.json();
}

export type LegacyContentMapUrls = {
  previewUrl: string;
  downloadUrl: string;
};

export type LegacyContentMapUrlsResponse = {
  success: boolean;
  data: LegacyContentMapUrls;
  error?: string;
};

/**
 * Presigned URLs for the Compliance Wave libraries content map PDF (legacy accounts only).
 */
export async function fetchLegacyContentMapUrls(accountId: string): Promise<LegacyContentMapUrls> {
  const url = buildComplianceEducationApiUrl(`/accounts/${accountId}/legacy-content-map`);
  const response = await http.get(url);

  if (!response.ok) {
    let errorText: string;
    try {
      const errorData = (await response.json()) as { error?: string };
      errorText = errorData.error || `HTTP ${response.status}`;
    } catch {
      errorText = response.statusText || `HTTP ${response.status}`;
    }
    throw new Error(errorText);
  }

  const body = (await response.json()) as LegacyContentMapUrlsResponse;
  if (!body.success || !body.data?.previewUrl) {
    throw new Error(body.error || 'Failed to load content map.');
  }
  return body.data;
}

/**
 * Updates library entitlements for an account
 */
export async function updateAccountLibraries(
  accountId: string,
  libraries: Array<{ id: string; hasAccess: boolean; translationAssigned: boolean }>,
): Promise<{ success: boolean }> {
  const url = buildComplianceEducationApiUrl(`/accounts/${accountId}/libraries`);

  const response = await http.put(url, { libraries } as unknown as BodyInit);

  if (!response.ok) {
    let errorText: string;
    try {
      errorText = await response.text();
    } catch (e) {
      errorText = response.statusText || `HTTP ${response.status}`;
    }
    throw new Error(`Failed to update account libraries: ${response.status} ${errorText}`);
  }

  return await response.json();
}

/**
 * Fetches assigned admins for a specific account/organization
 * Returns users from user_account_assignments table (not memberships)
 *
 * @param accountId - The unique identifier of the account
 * @param params - Pagination parameters
 * @returns Promise resolving to AssignedAdminsResponse
 * @throws Error if the request fails
 */
export async function fetchAssignedAdmins(
  accountId: string,
  params?: AccountUsersQueryParams,
): Promise<AssignedAdminsResponse> {
  const url = toComplianceEducationApiUrl(`/accounts/${accountId}/assigned-admins`);

  if (params?.page) {
    url.searchParams.append('page', params.page.toString());
  }
  if (params?.limit) {
    url.searchParams.append('limit', params.limit.toString());
  }
  if (params?.search) {
    url.searchParams.append('search', params.search);
  }
  if (params?.role) {
    url.searchParams.append('role', params.role);
  }
  if (params?.status) {
    url.searchParams.append('status', params.status);
  }

  const response = await http.get(url.toString());

  if (!response.ok) {
    let errorText: string;
    try {
      errorText = await response.text();
    } catch (e) {
      errorText = response.statusText || `HTTP ${response.status}`;
    }
    throw new Error(`Failed to fetch assigned admins: ${response.status} ${errorText}`);
  }

  return await response.json();
}

// ─── Organization settings (GET /accounts/{orgId}/settings) ─────────────────

/** One row from organization_branding_files in the settings API response */
export interface OrganizationSettingsBrandingFile {
  id: string;
  organizationId: string;
  filePath: string;
  originalFilename: string | null;
  contentType: string | null;
  fileSize: number | null;
  /** Pixel dimensions for logo files in `{width}X{height}` format (e.g. "120X80"). Null for documents or when unknown. */
  logoDimensions: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface OnboardingTrainingTopic {
  id: string;
  name: string;
}

export interface OrganizationSettingsData {
  organizationId: string;
  companyName: string | null;
  brandGuidelines: boolean;
  primaryBrandColor: string | null;
  secondaryBrandColor: string | null;
  documentFiles: OrganizationSettingsBrandingFile[];
  logoFiles: OrganizationSettingsBrandingFile[];
  // Onboarding fields (compliance_education accounts)
  companyWebsite?: string | null;
  industry?: string | null;
  employeeCount?: string | null;
  employeeTerminology?: string | null;
  speakUpSolution?: string | null;
  regions?: string[];
  brandSkipped?: boolean;
  trainingTopics?: OnboardingTrainingTopic[];
  priorityMonths?: string[];
  monthsToAvoid?: string[];
  selectedPackage?: 'essentials' | 'pro' | 'plus' | null;
  onboardingStatus?: string | null;
  onboardingStep?: number | null;
  onboardingCompletedAt?: string | null;
  onboardingCompletedBy?: string | null;
  onboardingSkipType?: 'deferred_later' | 'reassigned' | null;
  onboardingSkippedAt?: string | null;
  onboardingSkippedBy?: string | null;
  onboardingAssignedToUserId?: string | null;
  onboardingAssignedByUserId?: string | null;
  /** True when org is compliance_education and created on/after onboarding go-live. */
  onboardingEligible?: boolean;
  /** CEE-992 — whether the org opted in to AI training recommendations. */
  aiRecommendationEnabled?: boolean;
}

export interface OnboardingBannerStatusData {
  showBanner: boolean;
  inProgress: boolean;
  message: string;
}

export interface OnboardingBannerStatusResponse {
  success: boolean;
  data?: OnboardingBannerStatusData;
  error?: string;
}

export interface OrganizationSettingsResponse {
  success: boolean;
  data?: OrganizationSettingsData;
  error?: string;
  message?: string;
  code?: string;
}

export class OrganizationSettingsSaveError extends Error {
  readonly code?: string;

  readonly status?: number;

  constructor(message: string, options?: { code?: string; status?: number }) {
    super(message);
    this.name = 'OrganizationSettingsSaveError';
    this.code = options?.code;
    this.status = options?.status;
  }
}

function parseOrganizationSettingsErrorBody(
  errorText: string,
): { message: string; code?: string } {
  try {
    const parsed = JSON.parse(errorText) as {
      error?: string;
      message?: string;
      code?: string;
    };
    return {
      message: parsed.error || parsed.message || errorText,
      code: parsed.code,
    };
  } catch {
    return { message: errorText };
  }
}

/**
 * Fetches organization branding/settings for the edit form and read-only account tab.
 *
 * @throws Error on non-2xx HTTP or when the API returns success: false (or omits data).
 */
export async function fetchOrganizationSettings(accountId: string): Promise<OrganizationSettingsData> {
  const url = buildComplianceEducationApiUrl(`/accounts/${accountId}/settings`);

  const response = await http.get(url);

  if (!response.ok) {
    let errorText: string;
    try {
      errorText = await response.text();
    } catch (e) {
      errorText = response.statusText || `HTTP ${response.status}`;
    }
    throw new Error(`Failed to fetch organization settings: ${response.status} ${errorText}`);
  }

  const body = (await response.json()) as OrganizationSettingsResponse;
  if (!body.success || !body.data) {
    const detail = body.error || body.message || 'Organization settings response was not successful.';
    throw new Error(`Failed to fetch organization settings: ${detail}`);
  }
  return body.data;
}

/**
 * Lightweight poll for onboarding banner visibility on the Courses page.
 */
export async function fetchOnboardingBannerStatus(
  accountId: string,
): Promise<OnboardingBannerStatusData> {
  const url = buildComplianceEducationApiUrl(`/accounts/${accountId}/onboarding/banner-status`);
  const response = await http.get(url);

  if (!response.ok) {
    let errorText: string;
    try {
      errorText = await response.text();
    } catch (e) {
      errorText = response.statusText || `HTTP ${response.status}`;
    }
    throw new Error(`Failed to fetch onboarding banner status: ${response.status} ${errorText}`);
  }

  const body = (await response.json()) as OnboardingBannerStatusResponse;
  if (!body.success || !body.data) {
    const detail = body.error || 'Banner status response was not successful.';
    throw new Error(`Failed to fetch onboarding banner status: ${detail}`);
  }
  return body.data;
}

export type OnboardingSkipType = 'deferred_later' | 'reassigned';

export interface OnboardingSkipResponse {
  success: boolean;
  data?: {
    skipType: OnboardingSkipType;
    onboardingStatus: string;
  };
  error?: string;
}

export interface OnboardingReassignResponse {
  success: boolean;
  data?: {
    skipType: 'reassigned';
    assigneeUserId: string;
    emailQueued: boolean;
    recipientEmail?: string;
    /** Present when reassignment saved but PNS email was not queued (see CloudWatch audit logs). */
    emailError?: string;
  };
  error?: string;
}

/** POST /onboarding/skip — record "I'll do this later" without saving wizard data. */
export async function postOnboardingSkip(
  accountId: string,
  skipType: Extract<OnboardingSkipType, 'deferred_later'> = 'deferred_later',
): Promise<OnboardingSkipResponse['data']> {
  const url = buildComplianceEducationApiUrl(`/accounts/${accountId}/onboarding/skip`);
  const response = await http.post(url, JSON.stringify({ skipType }));

  const body = (await response.json()) as OnboardingSkipResponse;
  if (!response.ok || !body.success || !body.data) {
    const detail = body.error || `Failed to skip onboarding: ${response.status}`;
    throw new Error(detail);
  }
  return body.data;
}

export interface OnboardingReassignCandidate {
  id: string;
  email: string;
}

export interface OnboardingReassignCandidatesResponse {
  success: boolean;
  data?: {
    users: OnboardingReassignCandidate[];
  };
  error?: string;
}

/** GET /onboarding/reassign-candidates — org members for reassign picker (any active member). */
export async function fetchOnboardingReassignCandidates(
  accountId: string,
): Promise<OnboardingReassignCandidate[]> {
  const url = buildComplianceEducationApiUrl(
    `/accounts/${accountId}/onboarding/reassign-candidates`,
  );
  const response = await http.get(url);

  if (!response.ok) {
    let errorText: string;
    try {
      errorText = await response.text();
    } catch {
      errorText = response.statusText || `HTTP ${response.status}`;
    }
    throw new Error(`Failed to fetch reassign colleagues: ${response.status} ${errorText}`);
  }

  const body = (await response.json()) as OnboardingReassignCandidatesResponse;
  if (!body.success || !body.data) {
    const detail = body.error || 'Reassign colleagues response was not successful.';
    throw new Error(detail);
  }
  return body.data.users;
}

/** POST /onboarding/reassign — record "This isn't my task"; colleague assignee is required. */
export async function postOnboardingReassign(
  accountId: string,
  assigneeUserId: string,
): Promise<NonNullable<OnboardingReassignResponse['data']>> {
  const url = buildComplianceEducationApiUrl(`/accounts/${accountId}/onboarding/reassign`);
  const response = await http.post(
    url,
    JSON.stringify({ assigneeUserId }),
  );

  const body = (await response.json()) as OnboardingReassignResponse;
  if (!response.ok || !body.success || !body.data) {
    const detail = body.error || `Failed to reassign onboarding: ${response.status}`;
    throw new Error(detail);
  }
  return body.data;
}

export type OnboardingInviteValidationOutcome =
  | 'open_onboarding'
  | 'redirect_default'
  | 'expired'
  | 'invalid'
  | 'wrong_user';

export interface OnboardingInviteValidationData {
  outcome: OnboardingInviteValidationOutcome;
  message: string;
  assigneeUserId?: string;
}

/** GET /onboarding/invite/validate — validate reassign email deep link for current user. */
export async function validateOnboardingInvite(
  accountId: string,
  token: string,
): Promise<OnboardingInviteValidationData> {
  const url = buildComplianceEducationApiUrl(
    `/accounts/${accountId}/onboarding/invite/validate?token=${encodeURIComponent(token)}`,
  );
  const response = await http.get(url);
  const body = (await response.json()) as {
    success: boolean;
    data?: OnboardingInviteValidationData;
    error?: string;
  };

  if (!response.ok || !body.success || !body.data) {
    const detail = body.error || `Failed to validate onboarding invite: ${response.status}`;
    throw new Error(detail);
  }

  return body.data;
}

export interface OrganizationBrandingUploadUrlData {
  uploadUrl: string;
  relativePath: string;
  expiresIn: number;
}

/** Wire JSON from GET upload-url; logical failures use success: false and may omit data. */
export interface OrganizationBrandingUploadUrlResponse {
  success: boolean;
  data?: OrganizationBrandingUploadUrlData;
  error?: string;
  message?: string;
}

/**
 * GET presigned S3 PUT URL for organization branding (document or logo).
 * Client PUTs the file to uploadUrl, then includes data.relativePath in saveOrganizationSettings uploadedFiles.
 *
 * @throws Error on non-2xx HTTP or when the API returns success: false (or omits data).
 */
export async function fetchOrganizationBrandingUploadUrl(
  accountId: string,
  params: { filename: string; fileType: 'document' | 'logo' },
): Promise<OrganizationBrandingUploadUrlData> {
  const qs = new URLSearchParams({
    filename: params.filename,
    fileType: params.fileType,
  });
  const url = buildComplianceEducationApiUrl(
    `/accounts/${accountId}/settings/upload-url?${qs.toString()}`,
  );
  const response = await http.get(url);

  if (!response.ok) {
    let errorText: string;
    try {
      errorText = await response.text();
    } catch (e) {
      errorText = response.statusText || `HTTP ${response.status}`;
    }
    throw new Error(`Failed to get branding upload URL: ${response.status} ${errorText}`);
  }

  const body = (await response.json()) as OrganizationBrandingUploadUrlResponse;
  if (!body.success || !body.data) {
    const detail = body.error || body.message || 'Upload URL response was not successful.';
    throw new Error(`Failed to get branding upload URL: ${detail}`);
  }
  return body.data;
}

export interface OrganizationBrandingFileUrlData {
  downloadUrl: string;
  expiresIn: number;
}

/** Wire JSON from GET branding-file-url; logical failures use success: false and may omit data. */
export interface OrganizationBrandingFileUrlResponse {
  success: boolean;
  data?: OrganizationBrandingFileUrlData;
  error?: string;
  message?: string;
}

export type OrganizationBrandingFileDownloadUrlOptions = {
  /** When true, only for **logo** rows — presigned URL uses `Content-Disposition: inline` for `<img src>`. Documents reject `inline`. */
  inline?: boolean;
};

export class BrandingFileError extends Error {
  statusCode: number;
  errorType?: string;

  constructor(statusCode: number, message: string, errorType?: string) {
    super(message);
    this.name = 'BrandingFileError';
    this.statusCode = statusCode;
    this.errorType = errorType;
  }
}

/**
 * GET presigned S3 GET URL for an existing organization branding file (logo or guideline document).
 * Use `inline: true` only for logo thumbnails. Omit for file download (attachment disposition).
 *
 * @throws Error on non-2xx HTTP or when the API returns success: false (or omits data).
 */
export async function fetchOrganizationBrandingFileDownloadUrl(
  accountId: string,
  fileId: string,
  options?: OrganizationBrandingFileDownloadUrlOptions,
): Promise<OrganizationBrandingFileUrlData> {
  const qs = new URLSearchParams({ fileId });
  if (options?.inline) {
    qs.set('inline', 'true');
  }
  const url = buildComplianceEducationApiUrl(
    `/accounts/${accountId}/settings/branding-file-url?${qs.toString()}`,
  );
  const response = await http.get(url);

  if (!response.ok) {
    let errorText: string;
    let errorType: string | undefined;
    try {
      const errorData = await response.json();
      errorText = errorData.error || errorData.message || `HTTP ${response.status}`;
      errorType = errorData.errorType;
    } catch (e) {
      errorText = response.statusText || `HTTP ${response.status}`;
    }
    throw new BrandingFileError(response.status, errorText, errorType);
  }

  const body = (await response.json()) as OrganizationBrandingFileUrlResponse;
  if (!body.success || !body.data) {
    const detail = body.error || body.message || 'Branding file URL response was not successful.';
    throw new Error(`Failed to get branding file URL: ${detail}`);
  }
  return body.data;
}

export type OrganizationSettingsSavePayload = {
  companyName?: string;
  brandGuidelines?: boolean;
  primaryBrandColor?: string;
  secondaryBrandColor?: string;
  inactiveFileIds?: string[];
  fileStates?: { id: string; isActive: boolean }[];
  uploadedFiles?: {
    relativePath: string;
    fileType: 'document' | 'logo';
    originalFilename?: string;
    contentType?: string;
    fileSizeBytes?: number;
    /** Pixel dimensions for logo files in `{width}X{height}` format (e.g. "120X80"). Omit for documents. */
    logoDimensions?: string;
  }[];
  /** Onboarding wizard fields — triggers completion when present on POST /settings */
  companyWebsite?: string;
  industry?: string;
  employeeCount?: string;
  employeeTerminology?: string;
  speakUpSolution?: string;
  regions?: string[];
  brandSkipped?: boolean;
  trainingTopics?: OnboardingTrainingTopic[];
  priorityMonths?: string[];
  monthsToAvoid?: string[];
  additionalContext?: string;
  /** Step 2 plan selection — API name for DB column selected_package */
  selectedPackage?: 'essentials' | 'pro' | 'plus';
  /** Alias accepted by onboarding validation (mapped to selectedPackage). */
  trainingPackage?: 'essentials' | 'pro' | 'plus';
  /** CEE-992 — explicit opt-in for AI training recommendations */
  aiRecommendationEnabled?: boolean;
};

/**
 * POST JSON to persist settings after optional direct-to-S3 uploads (see fetchOrganizationBrandingUploadUrl).
 *
 * @throws Error on non-2xx HTTP or when the API returns success: false (or omits data).
 */
export async function saveOrganizationSettings(
  accountId: string,
  payload: OrganizationSettingsSavePayload,
): Promise<OrganizationSettingsData> {
  const url = buildComplianceEducationApiUrl(`/accounts/${accountId}/settings`);
  const response = await http.post(url, JSON.stringify(payload));

  if (!response.ok) {
    let errorText: string;
    try {
      errorText = await response.text();
    } catch (e) {
      errorText = response.statusText || `HTTP ${response.status}`;
    }
    const parsed = parseOrganizationSettingsErrorBody(errorText);
    throw new OrganizationSettingsSaveError(
      `Failed to save organization settings: ${response.status} ${parsed.message}`,
      { code: parsed.code, status: response.status },
    );
  }

  const body = (await response.json()) as OrganizationSettingsResponse;
  if (!body.success || !body.data) {
    const detail = body.error || body.message || 'Save response was not successful.';
    throw new OrganizationSettingsSaveError(`Failed to save organization settings: ${detail}`, {
      code: body.code,
      status: response.status,
    });
  }
  return body.data;
}

/** Partial update for completed onboarding (PUT /settings). Company profile fields only. */
export type OrganizationSettingsUpdatePayload = Pick<
  OrganizationSettingsSavePayload,
  | 'industry'
  | 'employeeCount'
  | 'regions'
  | 'employeeTerminology'
  | 'speakUpSolution'
  | 'aiRecommendationEnabled'
>;

export async function updateOrganizationSettings(
  accountId: string,
  payload: OrganizationSettingsUpdatePayload,
): Promise<OrganizationSettingsData> {
  const url = buildComplianceEducationApiUrl(`/accounts/${accountId}/settings`);
  const response = await http.put(url, JSON.stringify(payload));

  if (!response.ok) {
    let errorText: string;
    try {
      errorText = await response.text();
    } catch (e) {
      errorText = response.statusText || `HTTP ${response.status}`;
    }
    const parsed = parseOrganizationSettingsErrorBody(errorText);
    throw new OrganizationSettingsSaveError(
      `Failed to update organization settings: ${response.status} ${parsed.message}`,
      { code: parsed.code, status: response.status },
    );
  }

  const body = (await response.json()) as OrganizationSettingsResponse;
  if (!body.success || !body.data) {
    const detail = body.error || body.message || 'Update response was not successful.';
    throw new OrganizationSettingsSaveError(`Failed to update organization settings: ${detail}`, {
      code: body.code,
      status: response.status,
    });
  }
  return body.data;
}
